import React from "react";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Collapse,
    IconButton,
    LinearProgress,
    Stack,
    Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
    buildMonthKey,
    fmtEUR,
    formatDateDisplay,
    hasValue,
    UNKNOWN_MONTH_KEY,
    UNKNOWN_YEAR_KEY,
} from "./expenseManagementUtils";

export default function ExpenseList({
    loading,
    expenseMonthIndexLoaded,
    hasExpenseMonths,
    hasUnknownExpenses,
    handleOpenDialog,
    sortedYears,
    groupedExpenseMonths,
    expandedExpenseGroups,
    toggleYearExpanded,
    toggleMonthExpanded,
    expenseMonthData,
    loadingExpenseMonths,
    loadedExpenseMonths,
    handleOpenDetails,
    handleCopyExpense,
    openReceipt,
    hasReceipt,
    shouldSuggestAsset,
    handleSuggestAsset,
    copyingId,
}) {
    return (
        <>
            {loading && (
                <Box sx={{ mb: 2 }}>
                    <LinearProgress />
                </Box>
            )}

            <Grid2 container spacing={2}>
                {!loading && expenseMonthIndexLoaded && !hasExpenseMonths && !hasUnknownExpenses && (
                    <Grid2 xs={12}>
                        <Card sx={{ borderRadius: 3, boxShadow: "0 6px 24px rgba(0,0,0,0.06)" }}>
                            <CardContent>
                                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                    Noch keine Ausgaben vorhanden
                                </Typography>
                                <Typography sx={{ mt: 1.5, opacity: 0.8 }}>
                                    Lade deinen ersten Beleg hoch – per Klick oder einfach per Drag & Drop.
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<CloudUploadRoundedIcon />}
                                    onClick={handleOpenDialog}
                                    sx={{
                                        mt: 3,
                                        backgroundColor: "orange",
                                        color: "#000",
                                        fontWeight: 700,
                                        "&:hover": { backgroundColor: "#ff9e1a" },
                                    }}
                                >
                                    Jetzt hochladen
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid2>
                )}
            </Grid2>

            {sortedYears.map((year) => {
                const monthsInYear = groupedExpenseMonths[year] || [];

                return (
                    <Box key={year} sx={{ mb: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <IconButton
                                size="small"
                                onClick={() => toggleYearExpanded(year)}
                                aria-label={`Jahr ${year} ${
                                    expandedExpenseGroups?.years?.[year] ? "einklappen" : "ausklappen"
                                }`}
                            >
                                {expandedExpenseGroups?.years?.[year] ? (
                                    <ExpandLessRoundedIcon />
                                ) : (
                                    <ExpandMoreRoundedIcon />
                                )}
                            </IconButton>
                            <Typography variant="h6">{year}</Typography>
                        </Stack>

                        <Collapse in={!!expandedExpenseGroups?.years?.[year]} timeout="auto" unmountOnExit>
                            {monthsInYear.map((monthKey) => {
                                const monthStateKey = buildMonthKey(year, monthKey);
                                const monthList = expenseMonthData[monthStateKey] || [];
                                const isMonthExpanded = !!expandedExpenseGroups?.months?.[monthStateKey];
                                const monthLabel =
                                    monthKey === UNKNOWN_MONTH_KEY || year === UNKNOWN_YEAR_KEY
                                        ? UNKNOWN_MONTH_KEY
                                        : new Date(Number(year), Number(monthKey), 1).toLocaleDateString("de-DE", {
                                              month: "long",
                                          });

                                const isLoadingMonth = loadingExpenseMonths.has(monthStateKey);
                                const isMonthLoaded = loadedExpenseMonths.has(monthStateKey);

                                return (
                                    <Box key={monthStateKey} sx={{ mb: 2 }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => toggleMonthExpanded(year, monthKey)}
                                                aria-label={`${monthLabel} ${
                                                    isMonthExpanded ? "einklappen" : "ausklappen"
                                                }`}
                                            >
                                                {isMonthExpanded ? (
                                                    <ExpandLessRoundedIcon />
                                                ) : (
                                                    <ExpandMoreRoundedIcon />
                                                )}
                                            </IconButton>
                                            <Typography variant="subtitle1">{monthLabel}</Typography>
                                        </Stack>

                                        <Collapse in={isMonthExpanded} timeout="auto" unmountOnExit>
                                            {isLoadingMonth && (
                                                <Box sx={{ mb: 1 }}>
                                                    <LinearProgress />
                                                </Box>
                                            )}
                                            {!isLoadingMonth && isMonthLoaded && monthList.length === 0 && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Keine Ausgaben in diesem Monat.
                                                </Typography>
                                            )}
                                            {monthList.length > 0 && (
                                                <Grid2 container spacing={2}>
                                                    {monthList.map((expense) => {
                                                        const gross =
                                                            typeof expense.grossAmount === "number"
                                                                ? expense.grossAmount
                                                                : undefined;
                                                        const cat = expense.category;
                                                        const displayName = hasValue(expense.invoiceNumber)
                                                            ? expense.invoiceNumber
                                                            : expense.originalFilename || "Beleg";
                                                        return (
                                                            <Grid2 key={expense.id} xs={12} sm={6} md={4}>
                                                                <Card
                                                                    sx={{
                                                                        borderRadius: 3,
                                                                        boxShadow: (theme) =>
                                                                            theme.palette.mode === "dark"
                                                                                ? "0 6px 24px rgba(0,0,0,0.45)"
                                                                                : "0 6px 24px rgba(0,0,0,0.06)",
                                                                        border: (theme) =>
                                                                            `1px solid ${
                                                                                theme.palette.mode === "dark"
                                                                                    ? theme.palette.divider
                                                                                    : "#ffe0b2"
                                                                            }`,
                                                                        background: (theme) =>
                                                                            theme.palette.mode === "dark"
                                                                                ? theme.palette.background.paper
                                                                                : "linear-gradient(0deg, #fff8e1 0%, #fff 60%)",
                                                                        height: "100%",
                                                                        display: "flex",
                                                                        flexDirection: "column",
                                                                    }}
                                                                    >
                                                                        <CardContent sx={{ flex: 1 }}>
                                                                        <Stack
                                                                            direction="row"
                                                                            justifyContent="space-between"
                                                                            alignItems="flex-start"
                                                                        >
                                                                            <Typography
                                                                                variant="subtitle1"
                                                                                fontWeight={700}
                                                                                sx={{ pr: 1 }}
                                                                            >
                                                                                {displayName}
                                                                            </Typography>
                                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                                {expense.isEuService && (
                                                                                    <Chip
                                                                                        size="small"
                                                                                        label="EU"
                                                                                        color="info"
                                                                                        variant="outlined"
                                                                                    />
                                                                                )}
                                                                                {cat && <Chip size="small" label={cat} />}
                                                                            </Stack>
                                                                        </Stack>
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="text.secondary"
                                                                            sx={{ mt: 0.5 }}
                                                                        >
                                                                            Rechnungsdatum: {formatDateDisplay(expense.invoiceDate)}
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="text.secondary"
                                                                            sx={{ mt: 0.5 }}
                                                                        >
                                                                            Zahlungsdatum: {formatDateDisplay(expense.paymentDueDate)}
                                                                        </Typography>

                                                                        {(gross ?? null) !== null && (
                                                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                                Betrag: {fmtEUR(gross)}
                                                                            </Typography>
                                                                        )}

                                                                        {expense.supplierName && (
                                                                            <Typography
                                                                                variant="body2"
                                                                                color="text.secondary"
                                                                                sx={{ mt: 0.5 }}
                                                                            >
                                                                                Lieferant: {expense.supplierName}
                                                                            </Typography>
                                                                        )}
                                                                        {expense.taxExcludedWithdrawal && (
                                                                            <Chip
                                                                                size="small"
                                                                                color="success"
                                                                                variant="outlined"
                                                                                label="Entnahme (steuerfrei)"
                                                                                sx={{ mt: 1 }}
                                                                            />
                                                                        )}
                                                                    </CardContent>
                                                                    <CardActions sx={{ pt: 0, pb: 2, px: 2, gap: 1 }}>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            onClick={() => handleOpenDetails(expense)}
                                                                        >
                                                                            Details
                                                                        </Button>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            onClick={() => handleCopyExpense(expense)}
                                                                            disabled={copyingId === expense.id}
                                                                        >
                                                                            {copyingId === expense.id ? "Kopiere…" : "Kopieren"}
                                                                        </Button>
                                                                        {hasReceipt(expense) && (
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                onClick={() => openReceipt(expense)}
                                                                            >
                                                                                Beleg anzeigen
                                                                            </Button>
                                                                        )}
                                                                        {shouldSuggestAsset(expense) && (
                                                                            <Button
                                                                                size="small"
                                                                                variant="contained"
                                                                                color="warning"
                                                                                onClick={() => handleSuggestAsset(expense)}
                                                                            >
                                                                                Anlagegut vorschlagen
                                                                            </Button>
                                                                        )}
                                                                    </CardActions>
                                                                </Card>
                                                            </Grid2>
                                                        );
                                                    })}
                                                </Grid2>
                                            )}
                                        </Collapse>
                                    </Box>
                                );
                            })}
                        </Collapse>
                    </Box>
                );
            })}
        </>
    );
}
